import { getCommitmentInformationTableDataRetrieveLoader } from "gen/endpoints/commitment-information-table-data/commitment-information-table-data";
import { getFundFamilyDatesRetrieveLoader } from "gen/endpoints/dates/dates";
import { getFeeInformationListLoader } from "gen/endpoints/fee-information/fee-information";
import { getFundListLoader } from "gen/endpoints/fund-entities/fund-entities";
import { getGlobalEntityListLoader } from "gen/endpoints/global-entity/global-entity";
import { getInvestorClassRetrieveLoader } from "gen/endpoints/investor-class/investor-class";
import { getWaterfallConfigRetrieveLoader } from "gen/endpoints/waterfall/waterfall";
import i18n from "i18n/config";
import { RouteObject } from "react-router-dom";
import { composeLoaders } from "shared/utils/RoutingUtils";

export const fundFamilyConfigurationFundRoutes: RouteObject = {
  id: "fundFamilyConfigurationFund",
  path: "fund",
  children: [
    {
      id: "fundFamilyConfigurationFundIndex",
      handle: {
        showSidebar: true,
      },
      lazy: async () => {
        const { FundFamilyConfigurationFundPage } = await import(
          "features/fund-family/configuration/fund/FundFamilyConfigurationFundPage"
        );
        return { Component: FundFamilyConfigurationFundPage };
      },
      children: [
        {
          id: "fundFamilyConfigurationFundBasic",
          path: "",
          lazy: async () => {
            const { FundFamilyConfigurationFundTabProfile } = await import(
              "features/fund-family/configuration/fund/FundFamilyConfigurationFundTabProfile"
            );
            return { Component: FundFamilyConfigurationFundTabProfile };
          },
          handle: {
            title: i18n.t("fundFamily:setup.profile.title"),
          },
        },
        {
          id: "fundFamilyConfigurationFundDates",
          path: "dates",
          lazy: async () => {
            const { FundFamilyConfigurationFundTabDates } = await import(
              "features/fund-family/configuration/fund/FundFamilyConfigurationFundTabDates"
            );
            return { Component: FundFamilyConfigurationFundTabDates };
          },
          handle: {
            title: i18n.t("fundFamily:setup.dates.title"),
          },
          loader: getFundFamilyDatesRetrieveLoader(),
        },
        {
          id: "fundFamilyConfigurationFundFeesList",
          path: "fees",
          index: true,
          lazy: async () => {
            const { FundFamilyConfigurationFundTabFees } = await import(
              "features/fund-family/configuration/fund/FundFamilyConfigurationFundTabFees"
            );
            return { Component: FundFamilyConfigurationFundTabFees };
          },
          handle: {
            title: i18n.t("fundFamily:setup.fees.title"),
          },
          loader: composeLoaders(
            getFeeInformationListLoader(),
            getFundListLoader(),
          ),
        },
        {
          id: "fundFamilyConfigurationFundWaterfall",
          path: "waterfall",
          lazy: async () => {
            const { FundFamilyConfigurationFundTabWaterfall } = await import(
              "features/fund-family/configuration/fund/FundFamilyConfigurationFundTabWaterfall"
            );
            return { Component: FundFamilyConfigurationFundTabWaterfall };
          },
          handle: {
            title: i18n.t("fundFamily:setup.waterfall.title"),
          },
          loader: composeLoaders(
            getWaterfallConfigRetrieveLoader(),
            getFundListLoader(),
            getGlobalEntityListLoader(),
          ),
        },
        {
          id: "fundFamilyConfigurationFundClasses",
          path: "classes",
          lazy: async () => {
            const { FundFamilyConfigurationFundTabClasses } = await import(
              "features/fund-family/configuration/fund/FundFamilyConfigurationFundTabClasses"
            );
            return { Component: FundFamilyConfigurationFundTabClasses };
          },
          handle: {
            title: i18n.t("fundFamily:setup.classes.title"),
          },
        },
      ],
    },
    {
      id: "fundFamilyConfigurationFundFees",
      path: "fees",
      children: [
        {
          id: "fundFamilyConfigurationFundFeesAdd",
          path: "add",
          lazy: async () => {
            const { FundFamilyConfigurationFundFeesAddPage } = await import(
              "features/fund-family/configuration/fund/FundFamilyConfigurationFundFeesAddPage"
            );
            return { Component: FundFamilyConfigurationFundFeesAddPage };
          },
        },
        {
          id: "fundFamilyConfigurationFundFeesView",
          path: ":feeId",
          lazy: async () => {
            const { FundFamilyConfigurationFundFeesPage } = await import(
              "features/fund-family/configuration/fund/FundFamilyConfigurationFundFeesPage"
            );
            return { Component: FundFamilyConfigurationFundFeesPage };
          },
        },
        {
          id: "fundFamilyConfigurationFundFeesEdit",
          path: "edit/:feeId",
          lazy: async () => {
            const { FundFamilyConfigurationFundFeesEditPage } = await import(
              "features/fund-family/configuration/fund/FundFamilyConfigurationFundFeesEditPage"
            );
            return { Component: FundFamilyConfigurationFundFeesEditPage };
          },
        },
      ],
    },
    {
      id: "basisEdit",
      path: "fee-basis/:feeBasisId",
      lazy: async () => {
        const { FeeBasisEditPage } = await import(
          "features/calculation/FeeBasisEditPage"
        );
        return { Component: FeeBasisEditPage };
      },
    },
    {
      id: "classesEdit",
      path: "classes/:investorClassId/*",
      lazy: async () => {
        const { CommitmentClassSetupPage } = await import(
          "features/commitment-classes/setup/CommitmentClassSetupPage"
        );
        return { Component: CommitmentClassSetupPage };
      },
      children: [
        {
          id: "classesEditBasic",
          path: "",
          lazy: async () => {
            const { CommitmentClassTabBasicDetails } = await import(
              "features/commitment-classes/setup/CommitmentClassTabBasicDetails"
            );
            return { Component: CommitmentClassTabBasicDetails };
          },
          loader: composeLoaders(
            getInvestorClassRetrieveLoader(
              ({ fundFamilyId, investorClassId }) => ({
                fundFamilyId: fundFamilyId!,
                investorClassId: investorClassId!,
              }),
            ),
            getFundListLoader(),
          ),
          handle: {
            title: i18n.t("common:basicDetails"),
          },
        },
        {
          id: "classesEditFees",
          path: "fees",
          lazy: async () => {
            const { CommitmentClassTabFees } = await import(
              "features/commitment-classes/setup/CommitmentClassTabFees"
            );
            return { Component: CommitmentClassTabFees };
          },
          loader: composeLoaders(
            getFeeInformationListLoader(({ investorClassId }) => ({
              investor_class_id: investorClassId,
            })),
            getFundListLoader(),
          ),
          handle: {
            title: i18n.t("common:fees"),
          },
        },
        {
          id: "classesEditWaterfall",
          path: "waterfall",
          lazy: async () => {
            const { CommitmentClassTabWaterfall } = await import(
              "features/commitment-classes/setup/CommitmentClassTabWaterfall"
            );
            return { Component: CommitmentClassTabWaterfall };
          },
          loader: composeLoaders(
            getWaterfallConfigRetrieveLoader(({ investorClassId }) => ({
              investor_class_id: investorClassId,
            })),
            getFundListLoader(),
          ),
          handle: {
            title: i18n.t("common:waterfall"),
          },
        },
        {
          id: "classesEditCommitments",
          path: "commitments",
          lazy: async () => {
            const { CommitmentClassTabCommitments } = await import(
              "features/commitment-classes/setup/CommitmentClassTabCommitments"
            );
            return { Component: CommitmentClassTabCommitments };
          },
          loader: getCommitmentInformationTableDataRetrieveLoader(),
          handle: {
            title: i18n.t("common:commitments"),
          },
        },
      ],
    },
    {
      id: "classesFee",
      path: "classes/:investorClassId/fees/:feeId",
      lazy: async () => {
        const { CommitmentClassFeePage } = await import(
          "features/commitment-classes/setup/CommitmentClassFeePage"
        );
        return { Component: CommitmentClassFeePage };
      },
    },
    {
      id: "classesFeeOverride",
      path: "classes/:investorClassId/fees/:feeId/override",
      lazy: async () => {
        const { CommitmentClassSetupFeeOverridePage } = await import(
          "features/commitment-classes/setup/CommitmentClassSetupFeeOverridePage"
        );
        return { Component: CommitmentClassSetupFeeOverridePage };
      },
    },
  ],
};
